import type { User } from '../store/useAuthStore';

function trimOrEmpty(s: unknown): string {
  return typeof s === 'string' ? s.trim() : '';
}

export type ParticipantCertificateParts = {
  /** Titolo onorifico (opzionale), mostrato più piccolo sul certificato */
  title: string | null;
  /** Nome e cognome o nome completo (mai email) */
  nameLine: string;
};

/**
 * Parti del nome per il certificato: titolo opzionale; poi `first_name` + `last_name`,
 * oppure `full_name` se nome e cognome mancano.
 */
export function getParticipantCertificateParts(
  user: User | null | undefined
): ParticipantCertificateParts | null {
  if (user == null) return null;
  const titleRaw = trimOrEmpty(user.title);
  const title = titleRaw.length > 0 ? titleRaw : null;
  const first = trimOrEmpty(user.first_name);
  const last = trimOrEmpty(user.last_name);
  const combined = [first, last].filter(Boolean).join(' ');
  if (combined.length > 0) {
    return { title, nameLine: combined };
  }
  const fromFull = trimOrEmpty(user.full_name);
  if (fromFull.length > 0) {
    return { title, nameLine: fromFull };
  }
  return null;
}

/**
 * Stringa unica per controlli di idoneità / messaggi (titolo + nome, spazio).
 */
export function getParticipantFullName(
  user: User | null | undefined
): string | null {
  const parts = getParticipantCertificateParts(user);
  if (parts == null) return null;
  return [parts.title, parts.nameLine].filter(Boolean).join(' ');
}

export type ParticipantGivenFamilyNames = {
  givenName: string;
  familyName: string;
};

/**
 * Nome e cognome distinti per attestazioni (priorità a first_name / last_name; altrimenti spezza nameLine).
 */
export function getParticipantGivenFamilyNames(
  user: User | null | undefined,
  nameLineFromParts: string
): ParticipantGivenFamilyNames {
  const first = trimOrEmpty(user?.first_name);
  const last = trimOrEmpty(user?.last_name);
  if (first.length > 0 && last.length > 0) {
    return { givenName: first, familyName: last };
  }
  const line = nameLineFromParts.trim();
  const space = line.indexOf(' ');
  if (space === -1) {
    return { givenName: line, familyName: '' };
  }
  return {
    givenName: line.slice(0, space).trim(),
    familyName: line.slice(space + 1).trim(),
  };
}
